import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "wouter";
import { captureException } from "@/lib/telemetry";
import { Button } from "@/components/ui/button";

export function KundaliRouteLoadingState() {
  return (
    <div
      className="min-h-screen cosmic-bg flex items-center justify-center px-4"
      role="status"
      aria-live="polite"
      data-testid="kundali-route-loading"
    >
      <div className="max-w-sm text-center">
        <i className="fas fa-spinner fa-spin text-4xl text-primary" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-semibold">Loading your Kundali</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Preparing your birth-chart tools.
        </p>
      </div>
    </div>
  );
}

interface KundaliRouteRecoveryProps {
  children: ReactNode;
  onRetry: () => void;
}

interface KundaliRouteRecoveryState {
  error: Error | null;
}

export class KundaliRouteRecovery extends Component<
  KundaliRouteRecoveryProps,
  KundaliRouteRecoveryState
> {
  state: KundaliRouteRecoveryState = { error: null };

  static getDerivedStateFromError(error: Error): KundaliRouteRecoveryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      feature: "kundali",
      boundary: "route",
      componentStack: info.componentStack,
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        className="min-h-screen cosmic-bg flex items-center justify-center px-4"
        role="alert"
        data-testid="kundali-route-error"
      >
        <div className="max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-xl font-semibold">Kundali is temporarily unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We could not open the Kundali page. Your saved chart has not been changed.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button onClick={this.props.onRetry}>Try again</Button>
            <Button asChild variant="outline">
              <Link href="/home">Return home</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}